/**
 * `validation_screen` renderer — composes `@nextday/funnel-ui`
 * `ValidationScreen`. The screen auto-advances after `autoAdvanceMs`
 * (configurable from JSON, default 1800 ms — set 0 to disable).
 *
 * Chips can be supplied either:
 * - statically as `data.chips: string[]`, or
 * - dynamically as `data.chipsFromAnswers: string[]` — each entry is an
 *   answer key resolved from the current funnel answers map at render time
 *   so the screen echoes the user's earlier choices (per plan §5 step 8).
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "validation_screen",
 *     "data": {
 *       "headline": "Got it. Let's tune your match.",
 *       "message": "We'll personalize from here.",
 *       "emoji": "✨",
 *       "chipsFromAnswers": ["connection_intent", "first_conversation"],
 *       "autoAdvanceMs": 1800
 *     }
 *   }
 */
import { ValidationScreen } from "@nextday/funnel-ui";
import { z } from "zod";

import { buildChipsFromAnswers } from "@survey-kit/renderers/chips";
import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

export const validationScreenSchema = z
  .object({
    headline: z.string().min(1),
    message: z.string().optional(),
    emoji: z.string().optional(),
    chips: z.array(z.string().min(1)).optional(),
    chipsFromAnswers: z.array(z.string().min(1)).optional(),
    autoAdvanceMs: z.number().int().nonnegative().optional(),
  })
  .strict();

export type ValidationScreenData = z.infer<typeof validationScreenSchema>;

// Chip resolution is shared with `character_reveal_gate` via
// `@survey-kit/renderers/chips` — see that module for the contract.
const buildChips = buildChipsFromAnswers;

const ValidationScreenRenderer = ({
  data,
  step,
  answers,
  onAdvance,
}: RendererProps<ValidationScreenData>) => {
  const chips = buildChips(data, answers);

  return (
    <ValidationScreen
      headline={data.headline}
      message={data.message}
      emoji={data.emoji}
      chips={chips}
      autoAdvanceMs={data.autoAdvanceMs}
      onComplete={() => onAdvance()}
      testId={`renderer-validation-screen-${step.id}`}
    />
  );
};

export const validationScreenRenderer = createRenderer({
  type: "validation_screen",
  dataSchema: validationScreenSchema,
  label: "Validation screen",
  Component: ValidationScreenRenderer,
});

export default validationScreenRenderer;
